import { Outlet, Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { HipaaFooter } from './HipaaFooter';
import { NotificationBell } from './NotificationBell';
import { SkipToMain } from './SkipToMain';
import { OfflineBanner } from './OfflineBanner';
import { InstallPrompt } from './InstallPrompt';
import { useAuth } from '../stores/auth';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { phiText } from '../lib/phiConfig';

export function AppShell() {
  const { profile, org, loading, initialized } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Onboarding gate: org_admin without completed onboarding → force /onboarding wizard
  useEffect(() => {
    if (!initialized || loading) return;
    if (profile?.role === 'org_admin' && org && !org.onboarding_completed) {
      const allowedPaths = ['/onboarding', '/onboarding/baa', '/onboarding/complete'];
      if (!allowedPaths.includes(loc.pathname)) {
        nav('/onboarding', { replace: true });
      }
    }
  }, [initialized, loading, profile, org, loc.pathname, nav]);

  // Inactivity auto-logout (15 min)
  useEffect(() => {
    let timer;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        useAuth.getState().signOut().then(() => nav('/login?reason=inactivity'));
      }, 15 * 60 * 1000);
    };
    const events = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (timer) clearTimeout(timer);
    };
  }, [nav]);

  return (
    <div className="min-h-screen flex bg-[var(--color-warm-white)]">
      <SkipToMain />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <OfflineBanner />
        <header
          className="sticky top-0 z-40 bg-white/80 backdrop-blur-sm border-b border-[var(--color-border)] px-4 lg:px-6 py-2 flex items-center justify-between gap-3"
          style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top))' }}
          data-testid="app-topbar"
        >
          {/* Brand — visible where the sidebar is hidden (mobile/tablet) */}
          <Link to="/" className="mobile-only flex items-center gap-2 min-w-0" data-testid="topbar-brand">
            <img src="/om-icon.png" alt="Operon Management" className="h-8 w-8 object-contain shrink-0" />
            <span className="font-display font-semibold text-[var(--color-ink)] truncate">Operon</span>
          </Link>
          <div className="flex-1 hidden md:block" />
          <div className="hidden sm:flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-[var(--color-muted)]">
            <Shield size={11} className="text-[var(--color-teal)]" aria-hidden="true" />
            {phiText('HIPAA secured', 'Secured')}
          </div>
          <NotificationBell />
        </header>
        <main id="main-content" tabIndex="-1" className="flex-1 pb-24 md:pb-0 focus:outline-none">
          <Outlet />
        </main>
        <HipaaFooter />
      </div>
      <BottomNav />
      <InstallPrompt />
    </div>
  );
}
